/**
 * Contact Work capacity, daily plan, and operator accountability HTML.
 */
import { formatInteger, formatNumber, formatPercent } from "./format.js";
import { getDashboardOperator } from "./dashboard-auth.js";
import {
  detailMetric,
  escapeAttr,
  escapeHtml,
  labelize,
  pill,
  safeDateTimeLabel
} from "./dashboard-html.js";
import type {
  ContactWorkSavedViewFilters,
  LeadFactoryContactWorkAssigneeSummary,
  LeadFactoryContactWorkCapacityResponse,
  LeadFactoryContactWorkCompletionAssigneeSummary,
  LeadFactoryContactWorkOperatorEvent,
  LeadFactoryContactWorkOperatorEventsResponse,
  LeadFactoryContactWorkOperatorOutcome,
  LeadFactoryContactWorkOperatorOutcomesResponse,
  LeadFactoryContactWorkOperatorSummary,
  LeadFactoryContactWorkSummary,
  LeadFactoryContactWorkThroughputResponse,
  LeadFactoryContactWorkThroughputSummary,
  LeadFactoryTask
} from "./dashboard-lead-factory-types.js";

export const contactWorkDefaultPlanTargetDays = 2;
export const contactWorkMaxPlanTargetDays = 14;
export const contactWorkDefaultCallDailyCapacity = 100;
export const contactWorkDefaultLetterDailyCapacity = 250;
export const contactWorkDefaultResearchDailyCapacity = 25;
export const contactWorkMaxDailyCapacity = 1000;
export const contactWorkMaxOperatorRosterLength = 500;

export function normalizedContactWorkTargetDays(value: unknown): number {
  const days = Number(value ?? contactWorkDefaultPlanTargetDays);
  if (!Number.isInteger(days) || days <= 0) return contactWorkDefaultPlanTargetDays;
  return Math.min(days, contactWorkMaxPlanTargetDays);
}

export function normalizedContactWorkDailyCapacity(value: unknown, defaultCapacity: number): number {
  const capacity = Number(value ?? defaultCapacity);
  if (!Number.isInteger(capacity) || capacity <= 0) return defaultCapacity;
  return Math.min(capacity, contactWorkMaxDailyCapacity);
}

export function normalizedContactWorkOperatorRoster(value: unknown): string {
  return String(value ?? "").trim().slice(0, contactWorkMaxOperatorRosterLength);
}


export function isContactWorkTaskActionable(task: LeadFactoryTask): boolean {
  const readiness = task.contact_readiness || "";
  if ((task.campaign_actionability || "ready") !== "ready") return false;
  if (task.task_type === "skip_trace") return readiness === "needs_skip_trace";
  if (task.task_type === "pi_research") return readiness === "needs_pi";
  if (task.task_type === "call_owner") return readiness === "ready_for_call" || readiness === "ready_multi_channel";
  if (task.task_type === "prepare_letter") return readiness === "ready_for_letter" || readiness === "ready_multi_channel";
  return false;
}


export function contactWorkSummaryHtml(summary: LeadFactoryContactWorkSummary | undefined): string {
  if (!summary) return "";
  return `
    <div class="review-summary-grid contact-work-summary" data-contact-work-summary>
      ${detailMetric("Ready", formatInteger(summary.ready))}
      ${detailMetric("Waiting", formatInteger(summary.waiting))}
      ${detailMetric("Assigned", formatInteger(summary.assigned))}
      ${detailMetric("Unassigned", formatInteger(summary.unassigned))}
      ${detailMetric("Overdue", formatInteger(summary.overdue ?? 0))}
      ${detailMetric("Unscheduled", formatInteger(summary.unscheduled ?? 0))}
      ${detailMetric("Letters", formatInteger(summary.standardLetters))}
      ${detailMetric("Retry letters", formatInteger(summary.retryLetters))}
      ${detailMetric("Calls", formatInteger(summary.calls))}
      ${detailMetric("Skip trace", formatInteger(summary.skipTrace))}
      ${detailMetric("PI", formatInteger(summary.pi))}
    </div>
  `;
}

export function contactWorkCapacityHtml(capacity: LeadFactoryContactWorkCapacityResponse | undefined): string {
  const summary = capacity?.aggregateSummary ?? capacity?.summary;
  if (!summary) return "";
  const cappedLabel = capacity?.aggregateSummary
    ? capacity.capped
      ? `uncapped aggregate · sample capped at ${formatInteger(capacity.capacityLimit)}`
      : "uncapped aggregate"
    : capacity?.capped
      ? `showing first ${formatInteger(capacity.capacityLimit)}`
      : "full matching queue";
  return `
    <div class="contact-work-capacity" data-contact-work-capacity>
      <div class="operator-summary-header">
        <strong>Full queue capacity</strong>
        <span>${cappedLabel} · ${formatInteger(summary.loaded)} tasks</span>
      </div>
      <div class="review-summary-grid contact-work-capacity-grid">
        ${detailMetric("Total", formatInteger(summary.loaded))}
        ${detailMetric("Overdue", formatInteger(summary.overdue))}
        ${detailMetric("Unassigned", formatInteger(summary.unassigned))}
        ${detailMetric("Assigned", formatInteger(summary.assigned))}
        ${detailMetric("Letters", formatInteger(summary.standardLetters + summary.retryLetters))}
        ${detailMetric("Calls", formatInteger(summary.calls))}
        ${detailMetric("Research", formatInteger(summary.skipTrace + summary.pi))}
        ${detailMetric("Oldest", summary.oldestCreatedAt ? safeDateTimeLabel(summary.oldestCreatedAt) : "n/a")}
      </div>
      <div class="operator-summary-table">
        ${(summary.byAssignee ?? []).slice(0, 5).map(contactWorkCapacityAssigneeRowHtml).join("")}
      </div>
    </div>
  `;
}

function contactWorkCapacityAssigneeRowHtml(row: LeadFactoryContactWorkAssigneeSummary): string {
  return `
    <div class="operator-summary-row" data-contact-work-capacity-assignee-row data-contact-work-assignee="${escapeAttr(row.assignee)}">
      <strong>${escapeHtml(row.assignee === "unassigned" ? "Unassigned" : row.assignee)}</strong>
      <span>${formatInteger(row.loaded)} total</span>
      <span>${formatInteger(row.ready)} ready</span>
      <span>${formatInteger(row.overdue)} overdue</span>
      <span>${formatInteger(row.calls)} calls</span>
      <span>${formatInteger(row.letters + row.retryLetters)} letters</span>
      <span>${formatInteger(row.skipTrace + row.pi)} research</span>
    </div>
  `;
}

export function contactWorkCompletionPressureHtml(
  capacity: LeadFactoryContactWorkCapacityResponse | undefined,
  throughput: LeadFactoryContactWorkThroughputResponse | undefined
): string {
  const active = capacity?.aggregateSummary ?? capacity?.summary;
  const completed = throughput?.aggregateSummary ?? throughput?.summary;
  if (!active || !completed) return "";
  const readyNow = active.ready;
  const completedRecent = completed.completed;
  const denominator = readyNow + completedRecent;
  const completionShare = denominator > 0 ? completedRecent / denominator : null;
  const activeLetters = active.standardLetters + active.retryLetters;
  const completedLetters = completed.standardLetters + completed.retryLetters;
  const activeResearch = active.skipTrace + active.pi;
  const completedResearch = completed.skipTrace + completed.pi;
  return `
    <div class="contact-work-completion-pressure" data-contact-work-completion-pressure>
      <div class="operator-summary-header">
        <strong>Completion pressure</strong>
        <span>${formatInteger(completedRecent)} completed · ${formatInteger(readyNow)} ready now</span>
      </div>
      <div class="review-summary-grid contact-work-completion-pressure-grid">
        ${detailMetric("Close share", formatPercent(completionShare))}
        ${detailMetric("Ready now", formatInteger(readyNow))}
        ${detailMetric("Completed", formatInteger(completedRecent))}
        ${detailMetric("Overdue", formatInteger(active.overdue))}
        ${detailMetric("Calls", contactWorkDoneActiveLabel(completed.calls, active.calls))}
        ${detailMetric("Letters", contactWorkDoneActiveLabel(completedLetters, activeLetters))}
        ${detailMetric("Research", contactWorkDoneActiveLabel(completedResearch, activeResearch))}
      </div>
    </div>
  `;
}

export function contactWorkDailyPlanHtml(
  capacity: LeadFactoryContactWorkCapacityResponse | undefined,
  throughput: LeadFactoryContactWorkThroughputResponse | undefined,
  filters: ContactWorkSavedViewFilters
): string {
  const active = capacity?.aggregateSummary ?? capacity?.summary;
  const completed = throughput?.aggregateSummary ?? throughput?.summary;
  if (!active || !throughput || !completed) return "";
  const days = Math.max(1, Number(throughput.filters?.days ?? 7));
  const targetDays = normalizedContactWorkTargetDays(filters.targetDays);
  const laneCapacity = contactWorkLaneCapacity(filters);
  const dailyCompleted = completed.completed / days;
  const daysToClear = dailyCompleted > 0 ? active.ready / dailyCompleted : null;
  const lanePlan = contactWorkLanePlan(active, completed, days, targetDays, laneCapacity, filters);
  const bottleneck = lanePlan[0];
  const teamDailyCapacity = laneCapacity.call + laneCapacity.letter + laneCapacity.research;
  const totalCapacityGap = lanePlan.reduce((sum, lane) => sum + lane.capacityGap, 0);
  const bottleneckLabel = bottleneck
    ? `${bottleneck.label}: ${formatInteger(bottleneck.active)} open / ${contactWorkDailyRateLabel(bottleneck.daily)} day`
    : "n/a";
  const staffingSignal =
    active.ready === 0
      ? "No ready work"
      : totalCapacityGap > 0
        ? "Capacity gap"
      : dailyCompleted === 0
        ? "No recent completions"
        : daysToClear !== null && daysToClear > 5
          ? "Needs more capacity"
          : daysToClear !== null && daysToClear > 2
            ? "Watch capacity"
            : "Pace healthy";
  return `
    <div class="contact-work-daily-plan" data-contact-work-daily-plan>
      <div class="operator-summary-header">
        <strong>Work plan</strong>
        <span>${staffingSignal} · ${formatInteger(active.ready)} ready</span>
      </div>
      <div class="review-summary-grid contact-work-daily-plan-grid">
        ${detailMetric("Daily pace", contactWorkDailyRateLabel(dailyCompleted))}
        ${detailMetric("Days to clear", contactWorkDaysToClearLabel(daysToClear))}
        ${detailMetric("Target window", `${formatInteger(targetDays)}d`)}
        ${detailMetric("Team capacity", `${contactWorkDailyRateLabel(teamDailyCapacity)}/day`)}
        ${detailMetric("Capacity gap", contactWorkCapacityGapLabel(totalCapacityGap))}
        ${detailMetric("Overdue", formatInteger(active.overdue))}
        ${detailMetric("Bottleneck", escapeHtml(bottleneckLabel))}
      </div>
      <div class="operator-summary-table">
        ${lanePlan.map(contactWorkLanePlanRowHtml).join("")}
      </div>
    </div>
  `;
}

type ContactWorkLanePlanRow = {
  label: string;
  queue: string;
  letterKind: string;
  roster: string;
  active: number;
  completed: number;
  daily: number;
  targetDaily: number;
  teamDailyCapacity: number;
  capacityGap: number;
  paceGap: number;
  dispatchLimit: number;
  daysToClear: number | null;
};

type ContactWorkLaneCapacity = { call: number; letter: number; research: number };
type ContactWorkOperatorQuota = { assignee: string; limit: number };
type ContactWorkOperatorPlannedTarget = { assignee: string; planned: number; calls: number; letters: number; research: number };
type ContactWorkOperatorWorkloadRow = {
  assignee: string;
  planned: number;
  plannedCalls: number;
  plannedLetters: number;
  plannedResearch: number;
  activeReady: number;
  activeOverdue: number;
  activeCalls: number;
  activeLetters: number;
  activeResearch: number;
  completed: number;
  completedCalls: number;
  completedLetters: number;
  completedResearch: number;
  lastCompletedAt: string | null;
  status: string;
};

function contactWorkLaneCapacity(filters: ContactWorkSavedViewFilters): ContactWorkLaneCapacity {
  return {
    call: normalizedContactWorkDailyCapacity(filters.callDailyCapacity, contactWorkDefaultCallDailyCapacity),
    letter: normalizedContactWorkDailyCapacity(filters.letterDailyCapacity, contactWorkDefaultLetterDailyCapacity),
    research: normalizedContactWorkDailyCapacity(filters.researchDailyCapacity, contactWorkDefaultResearchDailyCapacity)
  };
}

function contactWorkLanePlan(
  active: LeadFactoryContactWorkSummary,
  completed: LeadFactoryContactWorkThroughputSummary,
  days: number,
  targetDays: number,
  laneCapacity: ContactWorkLaneCapacity,
  filters: ContactWorkSavedViewFilters
): ContactWorkLanePlanRow[] {
  return [
    { label: "Calls", queue: "call", letterKind: "all", active: active.calls, completed: completed.calls, teamDailyCapacity: laneCapacity.call, roster: filters.callOperatorRoster },
    { label: "Letters", queue: "letter", letterKind: "all", active: active.standardLetters + active.retryLetters, completed: completed.standardLetters + completed.retryLetters, teamDailyCapacity: laneCapacity.letter, roster: filters.letterOperatorRoster },
    { label: "Research", queue: "research", letterKind: "all", active: active.skipTrace + active.pi, completed: completed.skipTrace + completed.pi, teamDailyCapacity: laneCapacity.research, roster: filters.researchOperatorRoster }
  ]
    .map((lane) => {
      const daily = lane.completed / Math.max(1, days);
      const targetDaily = lane.active / Math.max(1, targetDays);
      const capacityGap = Math.max(0, targetDaily - lane.teamDailyCapacity);
      const paceGap = Math.max(0, targetDaily - daily);
      const dispatchBasis = Math.min(targetDaily, lane.teamDailyCapacity);
      return {
        ...lane,
        daily,
        targetDaily,
        capacityGap,
        paceGap,
        dispatchLimit: lane.active > 0 ? Math.min(lane.active, Math.max(1, Math.ceil(dispatchBasis || targetDaily))) : 0,
        daysToClear: daily > 0 ? lane.active / daily : lane.active > 0 ? null : 0
      };
    })
    .sort((left, right) => {
      const leftRank = left.daysToClear === null ? Number.POSITIVE_INFINITY : left.daysToClear;
      const rightRank = right.daysToClear === null ? Number.POSITIVE_INFINITY : right.daysToClear;
      if (rightRank !== leftRank) return rightRank - leftRank;
      return right.active - left.active;
    });
}

function contactWorkLanePlanRowHtml(row: ContactWorkLanePlanRow): string {
  const operatorPlan = contactWorkOperatorDispatchPlan(row.roster, row.dispatchLimit);
  return `
    <div class="operator-summary-row contact-work-plan-dispatch-row" data-contact-work-daily-plan-row data-contact-work-plan-lane="${escapeAttr(row.label.toLowerCase())}" data-contact-work-plan-queue="${escapeAttr(row.queue)}" data-contact-work-plan-letter-kind="${escapeAttr(row.letterKind)}" data-contact-work-plan-target-limit="${escapeAttr(String(row.dispatchLimit))}">
      <strong>${escapeHtml(row.label)}</strong>
      <span>${formatInteger(row.active)} open</span>
      <span>${contactWorkDailyRateLabel(row.daily)} day</span>
      <span>${contactWorkDailyRateLabel(row.targetDaily)} target</span>
      <span>${contactWorkDailyRateLabel(row.teamDailyCapacity)} cap</span>
      <span>${contactWorkCapacityGapLabel(row.capacityGap)} cap gap</span>
      <span>${contactWorkCapacityGapLabel(row.paceGap)} pace gap</span>
      <span>${contactWorkDaysToClearLabel(row.daysToClear)}</span>
      <button class="button secondary compact" type="button" data-contact-work-plan-assign data-contact-work-plan-queue="${escapeAttr(row.queue)}" data-contact-work-plan-letter-kind="${escapeAttr(row.letterKind)}" data-contact-work-plan-limit="${escapeAttr(String(row.dispatchLimit))}" data-contact-work-bulk-label="${escapeAttr(`${row.label} Work Plan target`)}" ${row.active === 0 ? "disabled" : ""}>Assign target</button>
      ${operatorPlan.length > 0 ? `<div class="contact-work-operator-dispatch" data-contact-work-operator-dispatch data-contact-work-operator-dispatch-lane="${escapeAttr(row.label.toLowerCase())}">
        ${operatorPlan.map((entry) => contactWorkOperatorDispatchButtonHtml(row, entry)).join("")}
      </div>` : ""}
    </div>
  `;
}

function contactWorkOperatorDispatchPlan(roster: string, laneDispatchLimit: number): ContactWorkOperatorQuota[] {
  const laneLimit = Math.max(0, Math.floor(laneDispatchLimit));
  if (laneLimit <= 0) return [];
  const entries: ContactWorkOperatorQuota[] = [];
  let remaining = laneLimit;
  for (const rawEntry of normalizedContactWorkOperatorRoster(roster).split(/[\n,]+/)) {
    const entry = rawEntry.trim();
    if (!entry || remaining <= 0) continue;
    const [rawAssignee, rawLimit] = entry.split(/[=:]/, 2);
    const assignee = rawAssignee?.trim() ?? "";
    if (!assignee) continue;
    const parsedLimit = Number(rawLimit?.trim() || remaining);
    const limit = Number.isFinite(parsedLimit) && parsedLimit > 0 ? Math.min(remaining, Math.ceil(parsedLimit)) : remaining;
    if (limit <= 0) continue;
    entries.push({ assignee, limit });
    remaining -= limit;
  }
  return entries;
}

function contactWorkOperatorDispatchButtonHtml(row: ContactWorkLanePlanRow, entry: ContactWorkOperatorQuota): string {
  return `
    <button
      class="button secondary compact"
      type="button"
      data-contact-work-operator-assign
      data-contact-work-plan-queue="${escapeAttr(row.queue)}"
      data-contact-work-plan-letter-kind="${escapeAttr(row.letterKind)}"
      data-contact-work-plan-limit="${escapeAttr(String(entry.limit))}"
      data-contact-work-operator-assignee="${escapeAttr(entry.assignee)}"
      data-contact-work-bulk-label="${escapeAttr(`${row.label} ${entry.assignee}`)}"
    >${escapeHtml(entry.assignee)} · ${formatInteger(entry.limit)}</button>
  `;
}

export function contactWorkOperatorAccountabilityHtml(
  capacity: LeadFactoryContactWorkCapacityResponse | undefined,
  throughput: LeadFactoryContactWorkThroughputResponse | undefined,
  filters: ContactWorkSavedViewFilters
): string {
  const active = capacity?.aggregateSummary ?? capacity?.summary;
  const completed = throughput?.aggregateSummary ?? throughput?.summary;
  if (!active || !throughput || !completed) return "";
  const days = Math.max(1, Number(throughput.filters?.days ?? 7));
  const targetDays = normalizedContactWorkTargetDays(filters.targetDays);
  const lanePlan = contactWorkLanePlan(active, completed, days, targetDays, contactWorkLaneCapacity(filters), filters);
  const rows = contactWorkOperatorWorkloadRows(lanePlan, active.byAssignee ?? [], completed.byAssignee ?? []);
  if (rows.length === 0) return "";
  const plannedTotal = rows.reduce((sum, row) => sum + row.planned, 0);
  const overdueTotal = rows.reduce((sum, row) => sum + row.activeOverdue, 0);
  const completedTotal = rows.reduce((sum, row) => sum + row.completed, 0);
  const escalationTotal = rows.filter((row) => contactWorkOperatorSlaEscalation(row).severity !== "stable").length;
  return `
    <div class="contact-work-operator-accountability" data-contact-work-operator-accountability>
      <div class="operator-summary-header">
        <strong>Operator accountability</strong>
        <span>${formatInteger(plannedTotal)} planned · ${formatInteger(completedTotal)} completed · ${formatInteger(overdueTotal)} overdue · ${formatInteger(escalationTotal)} escalate</span>
      </div>
      <div class="operator-summary-table">
        ${rows.slice(0, 10).map(contactWorkOperatorAccountabilityRowHtml).join("")}
      </div>
    </div>
  `;
}

function contactWorkOperatorWorkloadRows(
  lanePlan: ContactWorkLanePlanRow[],
  activeRows: LeadFactoryContactWorkAssigneeSummary[],
  completedRows: LeadFactoryContactWorkCompletionAssigneeSummary[]
): ContactWorkOperatorWorkloadRow[] {
  const plannedByAssignee = contactWorkPlannedOperatorTargets(lanePlan);
  const activeByAssignee = new Map(activeRows.map((row) => [row.assignee, row]));
  const completedByAssignee = new Map(completedRows.map((row) => [row.assignee, row]));
  const assignees = new Set<string>([...plannedByAssignee.keys(), ...activeByAssignee.keys(), ...completedByAssignee.keys()]);
  return Array.from(assignees)
    .map((assignee) => {
      const planned = plannedByAssignee.get(assignee);
      const active = activeByAssignee.get(assignee);
      const completed = completedByAssignee.get(assignee);
      const plannedTotal = planned?.planned ?? 0;
      const activeReady = active?.ready ?? 0;
      const activeOverdue = active?.overdue ?? 0;
      const completedTotal = completed?.completed ?? 0;
      return {
        assignee,
        planned: plannedTotal,
        plannedCalls: planned?.calls ?? 0,
        plannedLetters: planned?.letters ?? 0,
        plannedResearch: planned?.research ?? 0,
        activeReady,
        activeOverdue,
        activeCalls: active?.calls ?? 0,
        activeLetters: (active?.letters ?? 0) + (active?.retryLetters ?? 0),
        activeResearch: (active?.skipTrace ?? 0) + (active?.pi ?? 0),
        completed: completedTotal,
        completedCalls: completed?.calls ?? 0,
        completedLetters: (completed?.letters ?? 0) + (completed?.retryLetters ?? 0),
        completedResearch: (completed?.skipTrace ?? 0) + (completed?.pi ?? 0),
        lastCompletedAt: completed?.lastCompletedAt ?? null,
        status: contactWorkOperatorWorkloadStatus(plannedTotal, activeReady, activeOverdue, completedTotal)
      };
    })
    .sort((left, right) => {
      if (right.activeOverdue !== left.activeOverdue) return right.activeOverdue - left.activeOverdue;
      if (right.planned !== left.planned) return right.planned - left.planned;
      if (right.activeReady !== left.activeReady) return right.activeReady - left.activeReady;
      if (right.completed !== left.completed) return right.completed - left.completed;
      return left.assignee.localeCompare(right.assignee);
    });
}

function contactWorkPlannedOperatorTargets(lanePlan: ContactWorkLanePlanRow[]): Map<string, ContactWorkOperatorPlannedTarget> {
  const plannedByAssignee = new Map<string, ContactWorkOperatorPlannedTarget>();
  for (const lane of lanePlan) {
    for (const entry of contactWorkOperatorDispatchPlan(lane.roster, lane.dispatchLimit)) {
      const target = contactWorkPlannedOperatorTarget(plannedByAssignee, entry.assignee);
      target.planned += entry.limit;
      if (lane.queue === "call") target.calls += entry.limit;
      else if (lane.queue === "letter") target.letters += entry.limit;
      else target.research += entry.limit;
    }
  }
  return plannedByAssignee;
}

function contactWorkPlannedOperatorTarget(
  plannedByAssignee: Map<string, ContactWorkOperatorPlannedTarget>,
  assignee: string
): ContactWorkOperatorPlannedTarget {
  const existing = plannedByAssignee.get(assignee);
  if (existing) return existing;
  const created = { assignee, planned: 0, calls: 0, letters: 0, research: 0 };
  plannedByAssignee.set(assignee, created);
  return created;
}

function contactWorkOperatorWorkloadStatus(planned: number, activeReady: number, activeOverdue: number, completed: number): string {
  if (activeOverdue > 0) return "Overdue";
  if (planned > 0 && completed >= planned) return "Target closed";
  if (planned > 0 && activeReady > 0) return "In flight";
  if (planned > 0) return "Planned";
  if (activeReady > 0) return "Unplanned load";
  if (completed > 0) return "Recently closed";
  return "No active work";
}

function contactWorkOperatorAccountabilityRowHtml(row: ContactWorkOperatorWorkloadRow): string {
  const rebalanceLimit = contactWorkOperatorRebalanceLimit(row);
  const rebalanceLabel = row.activeOverdue > 0 ? "Rebalance overdue" : row.assignee === "unassigned" ? "Assign ready" : "Rebalance active";
  const escalation = contactWorkOperatorSlaEscalation(row);
  return `
    <div class="operator-summary-row operator-accountability-row" data-contact-work-operator-accountability-row data-contact-work-assignee="${escapeAttr(row.assignee)}">
      <strong>${escapeHtml(row.assignee === "unassigned" ? "Unassigned" : row.assignee)}</strong>
      <span>${formatInteger(row.planned)} planned</span>
      <span>${formatInteger(row.activeReady)} active</span>
      <span>${formatInteger(row.activeOverdue)} overdue</span>
      <span>${formatInteger(row.completed)} done</span>
      <span>${contactWorkOperatorLaneMixLabel(row)}</span>
      <span>${escapeHtml(row.status)}</span>
      <span class="operator-sla ${escapeAttr(escalation.severity)}" data-contact-work-sla-escalation="${escapeAttr(escalation.severity)}">${escapeHtml(escalation.label)}</span>
      <button
        class="button secondary compact"
        type="button"
        data-contact-work-operator-rebalance
        data-contact-work-rebalance-from="${escapeAttr(row.assignee)}"
        data-contact-work-rebalance-limit="${escapeAttr(String(rebalanceLimit))}"
        data-contact-work-bulk-label="${escapeAttr(`${rebalanceLabel} from ${row.assignee}`)}"
        ${rebalanceLimit <= 0 ? "disabled" : ""}
      >${escapeHtml(rebalanceLabel)}</button>
    </div>
  `;
}

type ContactWorkOperatorSlaEscalation = {
  label: string;
  severity: "critical" | "warning" | "attention" | "stable";
};

function contactWorkOperatorSlaEscalation(row: ContactWorkOperatorWorkloadRow): ContactWorkOperatorSlaEscalation {
  if (row.activeOverdue >= 5) return { label: "Escalate now", severity: "critical" };
  if (row.activeOverdue > 0) return { label: "Overdue watch", severity: "warning" };
  if (row.assignee === "unassigned" && row.activeReady > 0) return { label: "Needs owner", severity: "attention" };
  if (row.planned > 0 && row.activeReady > row.planned) return { label: "Over target", severity: "attention" };
  if (row.planned > 0 && row.completed >= row.planned) return { label: "Closed", severity: "stable" };
  if (row.planned > 0 && row.activeReady > 0) return { label: "On watch", severity: "stable" };
  if (row.activeReady > 0) return { label: "Monitor", severity: "stable" };
  return { label: "Stable", severity: "stable" };
}

function contactWorkOperatorRebalanceLimit(row: ContactWorkOperatorWorkloadRow): number {
  if (row.activeOverdue > 0) return row.activeOverdue;
  if (row.planned > 0 && row.activeReady > row.planned) return row.activeReady - row.planned;
  return row.activeReady;
}

function contactWorkOperatorLaneMixLabel(row: ContactWorkOperatorWorkloadRow): string {
  const calls = Math.max(row.plannedCalls, row.activeCalls, row.completedCalls);
  const letters = Math.max(row.plannedLetters, row.activeLetters, row.completedLetters);
  const research = Math.max(row.plannedResearch, row.activeResearch, row.completedResearch);
  return `C${formatInteger(calls)} L${formatInteger(letters)} R${formatInteger(research)}`;
}

export function contactWorkOperatorActionHistoryHtml(events: LeadFactoryContactWorkOperatorEventsResponse | undefined): string {
  if (!events) return "";
  const scopeLabel = events.assignedTo ? `for ${events.assignedTo}` : `last ${formatInteger(events.days)} days`;
  return `
    <div class="contact-work-operator-action-history" data-contact-work-operator-action-history>
      <div class="operator-summary-header">
        <strong>Action history</strong>
        <span>${escapeHtml(scopeLabel)}</span>
        <span>${formatInteger(events.events.length)} shown</span>
      </div>
      <div class="operator-action-history-list">
        ${events.events.slice(0, events.limit).map(contactWorkOperatorActionEventHtml).join("") || `<p class="empty">No Contact Work assignment actions yet.</p>`}
      </div>
    </div>
  `;
}

function contactWorkOperatorActionEventHtml(event: LeadFactoryContactWorkOperatorEvent): string {
  const moveLabel = contactWorkOperatorActionMoveLabel(event);
  const leadLabel = event.property_address || event.owner_name || event.property_lead_id || "unlinked lead";
  const taskLabel = [event.queue || event.task_type, event.letter_kind].filter(Boolean).join(" / ") || event.task_id || "task";
  return `
    <article class="operator-action-event" data-contact-work-operator-action-event>
      <strong>${escapeHtml(labelize(event.event_type))}</strong>
      <span>${escapeHtml(moveLabel)}</span>
      <small>${escapeHtml(taskLabel)} · ${escapeHtml(leadLabel)} · ${escapeHtml(event.actor)} · ${escapeHtml(event.created_at)}</small>
      ${event.note ? `<p>${escapeHtml(event.note)}</p>` : ""}
    </article>
  `;
}

function contactWorkOperatorActionMoveLabel(event: LeadFactoryContactWorkOperatorEvent): string {
  if (event.event_type === "contact_work_rebalanced") {
    return `${event.from_assigned_to || event.previous_assigned_to || "unassigned"} to ${event.to_assigned_to || "unassigned"}`;
  }
  return `assigned to ${event.assigned_to || "unassigned"}`;
}

export function contactWorkOperatorOutcomeReadbackHtml(outcomes: LeadFactoryContactWorkOperatorOutcomesResponse | undefined): string {
  if (!outcomes) return "";
  const scopeLabel = outcomes.operator ? `for ${outcomes.operator}` : `last ${formatInteger(outcomes.days)} days`;
  return `
    <div class="contact-work-operator-outcomes" data-contact-work-operator-outcomes>
      <div class="operator-summary-header">
        <strong>Outcome readback</strong>
        <span>${escapeHtml(scopeLabel)}</span>
        <span>${formatInteger(outcomes.outcomes.length)} recent</span>
      </div>
      <div class="operator-outcome-grid">
        ${outcomes.byOperator.slice(0, 6).map(contactWorkOperatorOutcomeSummaryHtml).join("") || `<p class="empty">No Contact Work outcomes recorded yet.</p>`}
      </div>
      <div class="operator-action-history-list">
        ${outcomes.outcomes.slice(0, 6).map(contactWorkOperatorOutcomeEventHtml).join("")}
      </div>
    </div>
  `;
}

function contactWorkOperatorOutcomeSummaryHtml(row: LeadFactoryContactWorkOperatorSummary): string {
  return `
    <div class="operator-outcome-summary" data-contact-work-operator-outcome-summary>
      <strong>${escapeHtml(row.operator || "unassigned")}</strong>
      <span>${formatInteger(row.total)} outcomes</span>
      <span>${contactWorkOperatorOutcomeMixLabel(row)}</span>
      <small>${formatInteger(row.connected)} connected · ${formatInteger(row.returnedMail)} returned · ${formatInteger(row.followUps)} follow-up</small>
    </div>
  `;
}

function contactWorkOperatorOutcomeEventHtml(outcome: LeadFactoryContactWorkOperatorOutcome): string {
  const leadLabel = outcome.property_address || outcome.owner_name || outcome.property_lead_id || "unlinked lead";
  return `
    <article class="operator-action-event" data-contact-work-operator-outcome-event>
      <strong>${escapeHtml(labelize(outcome.outcome))}</strong>
      <span>${escapeHtml(labelize(outcome.channel))} by ${escapeHtml(outcome.operator || "unassigned")}</span>
      <small>${escapeHtml(leadLabel)} · ${escapeHtml(outcome.attempted_at)}</small>
      ${outcome.note ? `<p>${escapeHtml(outcome.note)}</p>` : ""}
    </article>
  `;
}

function contactWorkOperatorOutcomeMixLabel(row: LeadFactoryContactWorkOperatorSummary): string {
  return `C${formatInteger(row.calls)} L${formatInteger(row.letters)} S${formatInteger(row.skipTrace)} P${formatInteger(row.pi)}`;
}

function contactWorkDailyRateLabel(value: number): string {
  return `${formatNumber(value, value >= 10 ? 0 : 1)}`;
}

function contactWorkDaysToClearLabel(value: number | null): string {
  if (value === null) return "no pace";
  if (value === 0) return "clear";
  return `${formatNumber(value, value >= 10 ? 0 : 1)}d`;
}

function contactWorkCapacityGapLabel(value: number): string {
  return value > 0 ? `+${contactWorkDailyRateLabel(value)}` : "on pace";
}

function contactWorkDoneActiveLabel(done: number, active: number): string {
  return `${formatInteger(done)} / ${formatInteger(active)}`;
}

export function contactWorkThroughputHtml(throughput: LeadFactoryContactWorkThroughputResponse | undefined): string {
  const summary = throughput?.aggregateSummary ?? throughput?.summary;
  if (!throughput || !summary) return "";
  const days = Number(throughput.filters?.days ?? 7);
  const cappedLabel = throughput.aggregateSummary
    ? throughput.capped
      ? `uncapped aggregate · sample capped at ${formatInteger(throughput.throughputLimit)}`
      : `uncapped aggregate · last ${formatInteger(days)} days`
    : throughput.capped
      ? `showing first ${formatInteger(throughput.throughputLimit)}`
      : `last ${formatInteger(days)} days`;
  return `
    <div class="contact-work-throughput" data-contact-work-throughput>
      <div class="operator-summary-header">
        <strong>Completed throughput</strong>
        <span>${cappedLabel} · ${formatInteger(summary.completed)} completed</span>
      </div>
      <div class="review-summary-grid contact-work-throughput-grid">
        ${detailMetric("Completed", formatInteger(summary.completed))}
        ${detailMetric("Calls", formatInteger(summary.calls))}
        ${detailMetric("Letters", formatInteger(summary.standardLetters + summary.retryLetters))}
        ${detailMetric("Research", formatInteger(summary.skipTrace + summary.pi))}
        ${detailMetric("Last", summary.lastCompletedAt ? safeDateTimeLabel(summary.lastCompletedAt) : "n/a")}
      </div>
      <div class="operator-summary-table">
        ${(summary.byAssignee ?? []).slice(0, 5).map(contactWorkThroughputAssigneeRowHtml).join("")}
      </div>
    </div>
  `;
}

function contactWorkThroughputAssigneeRowHtml(row: LeadFactoryContactWorkCompletionAssigneeSummary): string {
  return `
    <div class="operator-summary-row" data-contact-work-throughput-assignee-row data-contact-work-assignee="${escapeAttr(row.assignee)}">
      <strong>${escapeHtml(row.assignee === "unassigned" ? "Unassigned" : row.assignee)}</strong>
      <span>${formatInteger(row.completed)} completed</span>
      <span>${formatInteger(row.calls)} calls</span>
      <span>${formatInteger(row.letters + row.retryLetters)} letters</span>
      <span>${formatInteger(row.skipTrace + row.pi)} research</span>
      <span>last ${row.lastCompletedAt ? safeDateTimeLabel(row.lastCompletedAt) : "n/a"}</span>
    </div>
  `;
}

export function contactWorkOperatorSummaryHtml(summary: LeadFactoryContactWorkSummary | undefined): string {
  const rows = summary?.byAssignee ?? [];
  if (rows.length === 0) return "";
  return `
    <div class="contact-work-operator-summary" data-contact-work-operator-summary>
      <div class="operator-summary-header">
        <strong>Operator throughput</strong>
        <span>${formatInteger(summary?.overdue ?? 0)} overdue · oldest ${summary?.oldestCreatedAt ? safeDateTimeLabel(summary.oldestCreatedAt) : "n/a"}</span>
      </div>
      <div class="operator-summary-table">
        ${rows.slice(0, 8).map(contactWorkOperatorSummaryRowHtml).join("")}
      </div>
    </div>
  `;
}

function contactWorkOperatorSummaryRowHtml(row: LeadFactoryContactWorkAssigneeSummary): string {
  return `
    <div class="operator-summary-row" data-contact-work-assignee-row data-contact-work-assignee="${escapeAttr(row.assignee)}">
      <strong>${escapeHtml(row.assignee === "unassigned" ? "Unassigned" : row.assignee)}</strong>
      <span>${formatInteger(row.ready)} ready</span>
      <span>${formatInteger(row.waiting)} waiting</span>
      <span>${formatInteger(row.overdue)} overdue</span>
      <span>${formatInteger(row.calls)} calls</span>
      <span>${formatInteger(row.letters + row.retryLetters)} letters</span>
      <span>${formatInteger(row.skipTrace + row.pi)} research</span>
    </div>
  `;
}

export function contactWorkBulkAssignmentHtml(tasks: LeadFactoryTask[], capacity?: LeadFactoryContactWorkCapacityResponse): string {
  const readyTaskIds = contactWorkAssignableTaskIds(tasks);
  const unassignedTaskIds = contactWorkAssignableTaskIds(tasks.filter((task) => !task.assigned_to?.trim()));
  const fullReadyCount = capacity?.summary?.ready ?? 0;
  const capacityLabel = capacity?.capped ? `first ${formatInteger(capacity.capacityLimit)} ready-capable tasks` : "full filtered queue";
  return `
    <div class="contact-work-bulk-bar" data-contact-work-bulk-assignment>
      <input type="text" maxlength="120" value="${escapeAttr(getDashboardOperator() || "")}" placeholder="Assignee" aria-label="Contact Work assignee" data-contact-work-bulk-assignee>
      <button class="button secondary" type="button" data-contact-work-bulk-assign="${escapeAttr(unassignedTaskIds.join(","))}" data-contact-work-bulk-label="unassigned ready Contact Work" ${unassignedTaskIds.length === 0 ? "disabled" : ""}>Assign unassigned</button>
      <button class="button secondary" type="button" data-contact-work-bulk-assign="${escapeAttr(readyTaskIds.join(","))}" data-contact-work-bulk-label="loaded ready Contact Work" ${readyTaskIds.length === 0 ? "disabled" : ""}>Assign loaded</button>
      <button class="button secondary" type="button" data-contact-work-filter-assign data-contact-work-filter-limit="${escapeAttr(String(capacity?.capacityLimit ?? 1000))}" data-contact-work-bulk-label="full filtered Contact Work" ${fullReadyCount === 0 ? "disabled" : ""}>Assign full queue</button>
      <small>${formatInteger(unassignedTaskIds.length)} unassigned ready · ${formatInteger(readyTaskIds.length)} loaded ready · ${formatInteger(fullReadyCount)} ${capacityLabel}</small>
    </div>
  `;
}

export function contactWorkAssignableTaskIds(tasks: LeadFactoryTask[]): string[] {
  return tasks.filter((task) => task.id && isContactWorkTaskActionable(task)).map((task) => task.id);
}

